import { ICampaign, ICampaignItem } from '../types/campaigns';
import { IMskuOption, IOnDemandItem } from '../types/inventories';
import { isItemDigital, isItemMsku, isItemOnDemand, isItemOutOfStock } from './inventories';

export type TLocationContentItem = ICampaignItem | IOnDemandItem;

export type TLocationContentsIssue = 'digital' | 'out-of-stock' | 'insufficient-stock' | 'msku-empty' | null;

const getLocationLeafItems = (
  kits: ICampaign[] | null | undefined,
  items: TLocationContentItem[] | null | undefined,
): TLocationContentItem[] => [...(kits ?? []).flatMap((kit) => kit.items ?? []), ...(items ?? [])];

const isShortOfStock = (row: TLocationContentItem): boolean =>
  !row.preorder_allowed && (row.count ?? 0) < (row.quantity ?? 0);

export const getLocationContentsIssue = (
  kits: ICampaign[] | null | undefined,
  items: TLocationContentItem[] | null | undefined,
): TLocationContentsIssue => {
  const leaves = getLocationLeafItems(kits, items);

  if (leaves.some(isItemDigital)) {
    return 'digital';
  }

  let hasInsufficient = false;
  let hasMskuEmpty = false;

  for (const leaf of leaves) {
    if (isItemMsku(leaf) && leaf.sku_options?.length) {
      let total = 0;
      for (const option of leaf.sku_options) {
        const quantity = option.quantity ?? 0;
        total += quantity;
        if (quantity > 0 && isItemOutOfStock(option)) {
          return 'out-of-stock';
        }
        if (quantity > 0 && !option.preorder_allowed && (option.count ?? 0) < quantity) {
          hasInsufficient = true;
        }
      }
      if (total === 0) {
        hasMskuEmpty = true;
      }
      continue;
    }

    if (isItemOutOfStock(leaf)) {
      return 'out-of-stock';
    }
    if (!leaf.preorder_allowed && (leaf.count ?? 0) < (leaf.quantity ?? 0)) {
      hasInsufficient = true;
    }
  }

  if (hasInsufficient || buildLocationSendItems(kits, items).some(isShortOfStock)) {
    return 'insufficient-stock';
  }
  if (hasMskuEmpty) {
    return 'msku-empty';
  }

  return null;
};

export const getLocationRowKey = (row: TLocationContentItem): string => row.item_id || row.product_sku || '';

export const getMskuTotalQuantity = (item: Pick<ICampaignItem, 'sku_options'>): number =>
  (item.sku_options ?? []).reduce((sum, option) => sum + (option.quantity ?? 0), 0);

export const isMskuItemUnset = (item: ICampaignItem): boolean =>
  isItemMsku(item) &&
  (item.sku_options ?? []).length > 0 &&
  !isItemOutOfStock(item) &&
  getMskuTotalQuantity(item) === 0;

// A location send picks per option, so an MSKU only gets the option breakdown when it has options to pick.
export const hasMskuOptions = (
  item: Partial<Pick<ICampaignItem, 'type' | 'sku_options'>> | null | undefined,
): boolean => isItemMsku(item) && (item?.sku_options ?? []).length > 0;

const toLocationRow = (
  source: ICampaignItem | IMskuOption | IOnDemandItem,
  quantity: number,
  isHiddenForRecipient: boolean,
): TLocationContentItem => {
  const { base_quantity, is_customized, sku_options, ...rest } = source as ICampaignItem;

  return {
    ...rest,
    quantity,
    is_hidden_for_recipient: isHiddenForRecipient,
  } as TLocationContentItem;
};

export const buildLocationSendItems = (
  kits: ICampaign[] | null | undefined,
  looseItems: TLocationContentItem[] | null | undefined,
): TLocationContentItem[] => {
  const rows: TLocationContentItem[] = [];

  for (const leaf of getLocationLeafItems(kits, looseItems)) {
    if (isItemDigital(leaf) || isItemOnDemand(leaf as IOnDemandItem)) {
      continue;
    }

    if (isItemMsku(leaf) && leaf.sku_options?.length) {
      for (const option of leaf.sku_options) {
        const optionQuantity = option.quantity ?? 0;
        if (optionQuantity < 1 || isItemDigital(option)) {
          continue;
        }
        rows.push(toLocationRow(option, optionQuantity, false));
      }
      continue;
    }

    const quantity = leaf.quantity ?? 0;
    if (quantity < 1) {
      continue;
    }
    rows.push(toLocationRow(leaf, quantity, leaf.is_hidden_for_recipient ?? false));
  }

  const merged: TLocationContentItem[] = [];
  const indexByKey = new Map<string, number>();

  for (const row of rows) {
    const key = getLocationRowKey(row);
    const existingIndex = key ? indexByKey.get(key) : undefined;

    if (existingIndex === undefined) {
      if (key) {
        indexByKey.set(key, merged.length);
      }
      merged.push(row);
      continue;
    }

    merged[existingIndex] = {
      ...merged[existingIndex],
      quantity: (merged[existingIndex].quantity ?? 0) + (row.quantity ?? 0),
    } as TLocationContentItem;
  }

  return merged;
};
